/**
 * @fileoverview Revoke Invitation dialog.
 * @description Confirms revoking a PENDING or EXPIRED invitation and asks for the reason the
 * backend requires (POST /identity/invitations/{id}/revoke). The reasons come from GET
 * /identity/configuration, as the backend's integration guide asks, so the list never drifts from
 * what it accepts. The invitee's account stays INVITED, so the same email can be invited again.
 * Always mounted by its parent, so closing resets the chosen reason and the mutation.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useIdentityConfiguration } from '@/features/user-invitations/hooks/useIdentityConfiguration'
import { useRevokeInvitation } from '@/features/user-invitations/hooks/useRevokeInvitation'
import ModalFooterButton from '@/shared/components/atoms/ModalFooterButton'
import Modal from '@/shared/components/molecules/Modal'
import SelectField from '@/shared/components/molecules/SelectField'

import type {
  InvitationRevokeReason,
  InvitationSummary,
} from '@/features/user-invitations/types/user-invitations.types'

interface RevokeInvitationModalProps {
  invitation: InvitationSummary | null
  onClose: () => void
  onRevoked: (invitation: InvitationSummary) => void
}

interface RevokeFooterProps {
  onCancel: () => void
  onRevoke: () => void
  disabled: boolean
  isLoading: boolean
}

/**
 * @description Renders the dialog's Cancel and Revoke buttons.
 * @param {RevokeFooterProps} props - The two handlers and the Revoke button's state.
 * @param {() => void} props.onCancel - Dismisses the dialog.
 * @param {() => void} props.onRevoke - Revokes the invitation.
 * @param {boolean} props.disabled - Disables Revoke until a reason is chosen.
 * @param {boolean} props.isLoading - Whether the revoke request is in flight.
 */
function RevokeFooter({ onCancel, onRevoke, disabled, isLoading }: RevokeFooterProps) {
  const { t } = useTranslation()

  return (
    <>
      <ModalFooterButton
        type="button"
        onClick={onCancel}
        data-testid="revoke-invitation-cancel-button"
      >
        {t('userInvitations.cancel')}
      </ModalFooterButton>
      <ModalFooterButton
        type="button"
        variant="primary"
        onClick={onRevoke}
        data-testid="revoke-invitation-submit-button"
        disabled={disabled}
        isLoading={isLoading}
      >
        {t('userInvitations.revoke.submit')}
      </ModalFooterButton>
    </>
  )
}

/**
 * @component
 * @param {RevokeInvitationModalProps} props - The invitation to revoke (null while closed) and the
 * dialog's callbacks.
 * @param {InvitationSummary | null} props.invitation - The invitation, or null when closed.
 * @param {() => void} props.onClose - Dismisses the dialog.
 * @param {(invitation: InvitationSummary) => void} props.onRevoked - Called after a successful
 * revoke.
 */
function RevokeInvitationModal({ invitation, onClose, onRevoked }: RevokeInvitationModalProps) {
  const { t } = useTranslation()
  const configurationQuery = useIdentityConfiguration(invitation !== null)
  const revokeInvitation = useRevokeInvitation()
  const [reason, setReason] = useState('')
  const reasons = configurationQuery.data?.invitationRevokeReasons ?? []

  /**
   * @description Closes the dialog, clearing the chosen reason and any previous attempt first.
   */
  const handleClose = () => {
    setReason('')
    revokeInvitation.reset()
    onClose()
  }

  /**
   * @description Revokes the invitation with the chosen reason.
   */
  const handleRevoke = () => {
    if (!invitation || !reason) {
      return
    }

    revokeInvitation.mutate(
      { invitationId: invitation.invitationId, reason: reason as InvitationRevokeReason },
      {
        onSuccess: () => {
          handleClose()
          onRevoked(invitation)
        },
      },
    )
  }

  const error = revokeInvitation.error ?? configurationQuery.error

  return (
    <Modal
      data-testid="revoke-invitation-modal"
      open={invitation !== null}
      onClose={handleClose}
      title={t('userInvitations.revoke.title')}
      subtitle={t('userInvitations.revoke.description', { email: invitation?.email ?? '' })}
      footer={
        <RevokeFooter
          onCancel={handleClose}
          onRevoke={handleRevoke}
          disabled={!reason}
          isLoading={revokeInvitation.isPending}
        />
      }
    >
      <div className="flex flex-col gap-3">
        <SelectField
          id="revokeInvitationReason"
          data-testid="revoke-invitation-reason-select"
          label={`${t('userInvitations.revoke.reason')} *`}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          options={[
            { value: '', label: t('userInvitations.revoke.reasonPlaceholder') },
            ...reasons.map((option) => ({
              value: option.code,
              label: t(`userInvitations.revokeReasons.${option.code}`, {
                defaultValue: option.code,
              }),
            })),
          ]}
        />
        {error && (
          <p
            role="alert"
            className="text-[13px] text-danger"
            data-testid="revoke-invitation-error-message"
          >
            {error.message}
          </p>
        )}
      </div>
    </Modal>
  )
}

export default RevokeInvitationModal
